/**
 * Vehicle Configurator - Enhanced Progressive Enhancement
 * Enhances server-rendered content with interactivity for better SEO
 */

class VehicleConfigurator {
    constructor(container) {
        this.container = container;
        this.sectionId = container.dataset.sectionId;
        this.productId = container.dataset.productId;
        this.deliveryCharge = parseFloat(container.dataset.deliveryCharge) || 0;

        // State tracking
        this.state = {
            currentPackageIndex: 0,
            selectedVariantId: null,
            selectedVariantPrice: 0,
            selectedUpgrades: new Map(),
            selectedOptions: new Map(),
            basePrice: 0
        };

        this.init();
    }

    init() {
        // Find the first visible colors container (for the first package)
        const firstColorsContainer = this.container.querySelector('.vc-colors-container[data-package-index="0"]');

        if (firstColorsContainer) {
            // Look for active color, or select the first one if none is active
            let activeColor = firstColorsContainer.querySelector('.vc-color-swatch.is-active');

            if (!activeColor) {
                // If no active color, select the first one
                activeColor = firstColorsContainer.querySelector('.vc-color-swatch');
                if (activeColor) {
                    activeColor.classList.add('is-active');
                }
            }

            if (activeColor) {
                // Initialize state with the active color's data
                this.state.selectedVariantId = activeColor.dataset.variantId;
                this.state.selectedVariantPrice = parseFloat(activeColor.dataset.variantPrice) || 0;
                this.state.basePrice = this.state.selectedVariantPrice;

                // Update color name display
                const colorNameDisplay = firstColorsContainer.querySelector('[data-selected-color-name]');
                if (colorNameDisplay) {
                    colorNameDisplay.textContent = activeColor.dataset.color;
                }

                // Update variant image if available
                const variantImage = activeColor.dataset.variantImage;
                if (variantImage) {
                    const variantImageEl = document.querySelector('[data-variant-image]');
                    if (variantImageEl) {
                        variantImageEl.src = variantImage;
                        variantImageEl.alt = activeColor.dataset.color;
                    }

                    const mainImage = document.querySelector('[data-main-image]');
                    if (mainImage) {
                        mainImage.src = variantImage;
                        mainImage.alt = activeColor.dataset.color;
                    }
                }
            }
        }

        // Auto-select first option in each group for the first package (index 0)
        // EXCEPT for accessories and options with extra price
        const firstOptionsContainer = this.container.querySelector('.vc-options-container[data-package-index="0"]');
        if (firstOptionsContainer) {
            const groups = new Set();
            firstOptionsContainer.querySelectorAll('.vc-option-card').forEach(card => {
                const category = card.dataset.category;
                const group = card.dataset.group;
                const price = parseFloat(card.dataset.optionPrice) || 0;
                const key = `${category}::${group}`;

                // Skip auto-selection for accessories or options with extra price
                const isAccessory = category === 'accessory' || category === 'accessories';
                const hasExtraPrice = price > 0;

                if (!groups.has(key)) {
                    groups.add(key);

                    // Only auto-select if NOT accessory AND no extra price
                    if (!isAccessory && !hasExtraPrice) {
                        card.classList.add('is-active');
                        card.classList.add('is-selected');

                        // Add to state
                        this.state.selectedOptions.set(key, {
                            id: card.dataset.optionId,
                            name: card.dataset.optionName,
                            price: price,
                            category: category,
                            group: group
                        });
                    }
                }
            });
        }

        this.attachEventListeners();
        this.updatePricingSummary();
    }

    attachEventListeners() {
        document.querySelectorAll('.vc-gallery-thumbnail').forEach(thumb => {
            thumb.addEventListener('click', (e) => this.handleThumbnailClick(e));
        });

        this.container.querySelectorAll('[data-action="toggle-package"]').forEach(trigger => {
            trigger.addEventListener('click', (e) => this.handlePackageToggle(e));
        });

        this.container.querySelectorAll('[data-action="select-color"]').forEach(swatch => {
            swatch.addEventListener('click', (e) => this.handleColorSelect(e));
        });

        this.container.querySelectorAll('[data-action="toggle-upgrade"]').forEach(checkbox => {
            checkbox.addEventListener('change', (e) => this.handleUpgradeToggle(e));
        });

        this.container.querySelectorAll('[data-action="select-option"]').forEach(card => {
            card.addEventListener('click', (e) => this.handleOptionSelect(e));
        });

        this.container.querySelectorAll('[data-action="show-modal"]').forEach(btn => {
            btn.addEventListener('click', (e) => this.handleModalOpen(e));
        });

        this.setupModalHandlers();
    }

    handleThumbnailClick(e) {
        const thumb = e.currentTarget;
        const imgSrc = thumb.querySelector('img').src;
        const mainImage = document.querySelector('[data-main-image]');

        if (mainImage) {
            mainImage.src = imgSrc;
        }

        document.querySelectorAll('.vc-gallery-thumbnail').forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
    }

    handlePackageToggle(e) {
        const trigger = e.currentTarget;
        const accordionItem = trigger.closest('.vc-accordion-item');
        const content = accordionItem.querySelector('.vc-accordion-content');
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
        const packageIndex = parseInt(accordionItem.dataset.packageIndex);

        this.container.querySelectorAll('.vc-accordion-item').forEach(item => {
            if (item !== accordionItem) {
                item.classList.remove('is-active');
                const otherTrigger = item.querySelector('.vc-accordion-trigger');
                const otherContent = item.querySelector('.vc-accordion-content');
                otherTrigger.setAttribute('aria-expanded', 'false');
                otherContent.hidden = true;
            }
        });

        if (isExpanded) {
            accordionItem.classList.remove('is-active');
            trigger.setAttribute('aria-expanded', 'false');
            content.hidden = true;
        } else {
            accordionItem.classList.add('is-active');
            trigger.setAttribute('aria-expanded', 'true');
            content.hidden = false;
            this.state.currentPackageIndex = packageIndex;
            this.switchPackageContent(packageIndex);
        }
    }

    switchPackageContent(packageIndex) {
        document.querySelectorAll('.vc-colors-container').forEach(container => {
            container.style.display = 'none';
        });
        document.querySelectorAll('.vc-upgrades-container').forEach(container => {
            container.style.display = 'none';
        });
        document.querySelectorAll('.vc-options-container').forEach(container => {
            container.style.display = 'none';
        });

        const selectedColorsContainer = document.querySelector(`.vc-colors-container[data-package-index="${packageIndex}"]`);
        const selectedUpgradesContainer = document.querySelector(`.vc-upgrades-container[data-package-index="${packageIndex}"]`);
        const selectedOptionsContainer = document.querySelector(`.vc-options-container[data-package-index="${packageIndex}"]`);

        if (selectedColorsContainer) {
            selectedColorsContainer.style.display = 'block';

            // Remove active state from all color swatches in this container
            selectedColorsContainer.querySelectorAll('.vc-color-swatch').forEach(s => s.classList.remove('is-active'));

            // Select the first color variant by default
            const firstColor = selectedColorsContainer.querySelector('.vc-color-swatch');
            if (firstColor) {
                firstColor.classList.add('is-active');

                // Update state with first variant
                this.state.selectedVariantId = firstColor.dataset.variantId;
                this.state.selectedVariantPrice = parseFloat(firstColor.dataset.variantPrice) || 0;
                this.state.basePrice = this.state.selectedVariantPrice;

                // Update color name display
                const colorNameDisplay = selectedColorsContainer.querySelector('[data-selected-color-name]');
                if (colorNameDisplay) {
                    colorNameDisplay.textContent = firstColor.dataset.color;
                }

                // Update variant image if available
                const variantImage = firstColor.dataset.variantImage;
                if (variantImage) {
                    // Update single variant image mode
                    const variantImageEl = document.querySelector('[data-variant-image]');
                    if (variantImageEl) {
                        variantImageEl.src = variantImage;
                        variantImageEl.alt = firstColor.dataset.color;
                    }

                    // Update full gallery mode
                    const mainImage = document.querySelector('[data-main-image]');
                    if (mainImage) {
                        mainImage.src = variantImage;
                        mainImage.alt = firstColor.dataset.color;
                    }
                }
            }
        }
        if (selectedUpgradesContainer) {
            const hasUpgrades = selectedUpgradesContainer.querySelectorAll('.vc-upgrade-item').length > 0;
            if (hasUpgrades) {
                selectedUpgradesContainer.style.display = 'block';
                this.state.selectedUpgrades.clear();
                selectedUpgradesContainer.querySelectorAll('.vc-upgrade-checkbox').forEach(checkbox => {
                    checkbox.checked = false;
                });
            } else {
                selectedUpgradesContainer.style.display = 'none';
            }
        }
        if (selectedOptionsContainer) {
            const hasOptions = selectedOptionsContainer.querySelectorAll('.vc-option-card').length > 0;
            if (hasOptions) {
                selectedOptionsContainer.style.display = 'block';
                this.state.selectedOptions.clear();
                selectedOptionsContainer.querySelectorAll('.vc-option-card').forEach(card => {
                    card.classList.remove('is-active');
                    card.classList.remove('is-selected');
                });

                // Auto-select first option in each group, EXCEPT for accessories and options with extra price
                const groups = new Set();
                selectedOptionsContainer.querySelectorAll('.vc-option-card').forEach(card => {
                    const category = card.dataset.category;
                    const group = card.dataset.group;
                    const price = parseFloat(card.dataset.optionPrice) || 0;
                    const key = `${category}::${group}`;

                    // Skip auto-selection for accessories or options with extra price
                    const isAccessory = category === 'accessory' || category === 'accessories';
                    const hasExtraPrice = price > 0;

                    if (!groups.has(key)) {
                        groups.add(key);

                        // Only auto-select if NOT accessory AND no extra price
                        if (!isAccessory && !hasExtraPrice) {
                            card.classList.add('is-active');
                            card.classList.add('is-selected');

                            // Add to state
                            this.state.selectedOptions.set(key, {
                                id: card.dataset.optionId,
                                name: card.dataset.optionName,
                                price: price,
                                category: category,
                                group: group
                            });
                        }
                    }
                });
            } else {
                selectedOptionsContainer.style.display = 'none';
            }
        }

        this.updatePricingSummary();
    }

    handleColorSelect(e) {
        const swatch = e.currentTarget;
        const colorName = swatch.dataset.color;
        const variantId = swatch.dataset.variantId;
        const variantPrice = parseFloat(swatch.dataset.variantPrice) || 0;
        const variantImage = swatch.dataset.variantImage;

        const colorsContainer = swatch.closest('.vc-colors-container');
        colorsContainer.querySelectorAll('.vc-color-swatch').forEach(s => s.classList.remove('is-active'));
        swatch.classList.add('is-active');

        const colorNameDisplay = colorsContainer.querySelector('[data-selected-color-name]');
        if (colorNameDisplay) {
            colorNameDisplay.textContent = colorName;
        }

        this.state.selectedVariantId = variantId;
        this.state.selectedVariantPrice = variantPrice;
        this.state.basePrice = variantPrice;

        // Update URL with variant query parameter
        if (variantId && window.history && window.history.replaceState) {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', variantId);
            window.history.replaceState({}, '', url.toString());
        }

        // Update image in single variant image mode
        const variantImageEl = document.querySelector('[data-variant-image]');
        if (variantImageEl && variantImage) {
            variantImageEl.src = variantImage;
            variantImageEl.alt = colorName;
        }

        // Update image in full gallery mode (for theme's gallery)
        if (variantImage) {
            const mainImage = document.querySelector('[data-main-image]');
            if (mainImage) {
                mainImage.src = variantImage;
                mainImage.alt = colorName;
            }
        }

        this.updatePricingSummary();
    }

    handleUpgradeToggle(e) {
        const checkbox = e.currentTarget;
        const upgradeId = checkbox.dataset.upgradeId;
        const upgradeName = checkbox.dataset.upgradeName;
        const upgradePrice = parseFloat(checkbox.dataset.upgradePrice) || 0;

        if (checkbox.checked) {
            this.state.selectedUpgrades.set(upgradeId, {
                name: upgradeName,
                price: upgradePrice
            });
        } else {
            this.state.selectedUpgrades.delete(upgradeId);
        }

        this.updatePricingSummary();
    }

    handleOptionSelect(e) {
        const card = e.currentTarget;

        if (e.target.closest('[data-action="show-modal"]')) {
            return;
        }

        const optionId = card.dataset.optionId;
        const optionName = card.dataset.optionName;
        const optionPrice = parseFloat(card.dataset.optionPrice) || 0;
        const category = card.dataset.category;
        const group = card.dataset.group;
        const key = `${category}::${group}`;

        const groupCards = this.container.querySelectorAll(
            `[data-action="select-option"][data-category="${category}"][data-group="${group}"]`
        );

        // Check if this option is optional (accessory or has extra price)
        const isAccessory = category === 'accessory' || category === 'accessories';
        const hasExtraPrice = optionPrice > 0;
        const isOptional = isAccessory || hasExtraPrice;

        // Check if clicking an already-selected card
        const isAlreadySelected = card.classList.contains('is-active');

        // If optional and already selected, allow deselection
        if (isOptional && isAlreadySelected) {
            card.classList.remove('is-active');
            card.classList.remove('is-selected');
            this.state.selectedOptions.delete(key);
        } else {
            // Remove active state from all cards in the same group
            groupCards.forEach(c => {
                c.classList.remove('is-active');
                c.classList.remove('is-selected');
            });

            // Add active state to clicked card
            card.classList.add('is-active');
            card.classList.add('is-selected');

            // Update state
            this.state.selectedOptions.set(key, {
                id: optionId,
                name: optionName,
                price: optionPrice,
                category: category,
                group: group
            });
        }

        this.updatePricingSummary();
    }

    updatePricingSummary() {
        const basePriceEl = this.container.querySelector('[data-base-price]');
        const upgradesPriceEl = this.container.querySelector('[data-upgrades-price]');
        const upgradesPriceRow = this.container.querySelector('[data-upgrades-row]');
        const optionsPriceEl = this.container.querySelector('[data-options-price]');
        const optionsPriceRow = this.container.querySelector('[data-options-row]');
        const totalPriceEl = this.container.querySelector('[data-total-price]');

        if (!basePriceEl || !totalPriceEl) return;

        let upgradesTotal = 0;
        this.state.selectedUpgrades.forEach(upgrade => {
            upgradesTotal += upgrade.price;
        });

        let optionsTotal = 0;
        this.state.selectedOptions.forEach(option => {
            optionsTotal += option.price;
        });

        basePriceEl.textContent = this.formatMoney(this.state.basePrice);

        if (upgradesTotal > 0) {
            if (upgradesPriceRow) upgradesPriceRow.style.display = '';
            if (upgradesPriceEl) upgradesPriceEl.textContent = this.formatMoney(upgradesTotal);
        } else {
            if (upgradesPriceRow) upgradesPriceRow.style.display = 'none';
        }

        if (optionsTotal > 0) {
            if (optionsPriceRow) optionsPriceRow.style.display = '';
            if (optionsPriceEl) optionsPriceEl.textContent = this.formatMoney(optionsTotal);
        } else {
            if (optionsPriceRow) optionsPriceRow.style.display = 'none';
        }

        const total = this.state.basePrice + upgradesTotal + optionsTotal + this.deliveryCharge;
        totalPriceEl.textContent = this.formatMoney(total);
    }

    handleModalOpen(e) {
        e.stopPropagation();
        const btn = e.currentTarget;
        const parent = btn.parentElement;

        const titleEl = parent.querySelector('.vc-modal-title-text');
        const title = titleEl ? titleEl.textContent : '';

        const imageEl = parent.querySelector('.vc-modal-image-url');
        const image = imageEl ? imageEl.textContent : '';

        const contentDiv = parent.querySelector('.vc-modal-content-hidden');
        const content = contentDiv ? contentDiv.innerHTML : '';

        this.showModal(title, content, image);
    }

    setupModalHandlers() {
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.closeModal();
            }
        });
    }

    showModal(title, content, image) {
        const modal = document.querySelector('[data-modal]');
        if (!modal) return;

        const titleEl = modal.querySelector('[data-modal-title]');
        const contentEl = modal.querySelector('[data-modal-content]');
        const imageContainer = modal.querySelector('[data-modal-image-container]');
        const imageEl = modal.querySelector('[data-modal-image-el]');

        if (titleEl) titleEl.textContent = title || '';
        if (contentEl) contentEl.innerHTML = content || '';

        if (image && imageContainer && imageEl) {
            imageEl.src = image;
            imageEl.alt = title || '';
            imageContainer.hidden = false;
        } else if (imageContainer) {
            imageContainer.hidden = true;
        }

        modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';

        modal.querySelectorAll('[data-modal-close]').forEach(el => {
            el.addEventListener('click', () => this.closeModal());
        });
    }

    closeModal() {
        const modal = document.querySelector('[data-modal]');
        if (modal) {
            modal.classList.remove('is-open');
            document.body.style.overflow = '';
        }
    }

    formatMoney(amount) {
        const num = parseFloat(amount);
        if (isNaN(num)) return '$0.00';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(num / 100);
    }

    getConfigurationSummary() {
        const config = [];

        // Get selected package
        const packageAccordion = this.container.querySelector('.vc-accordion-item.is-active[data-package-id]');
        if (packageAccordion) {
            const packageName = packageAccordion.querySelector('.vc-accordion-title')?.textContent.trim();
            if (packageName) config.push(`Package: ${packageName}`);
        }

        // Get selected color
        const activeColor = this.container.querySelector('.vc-color-swatch.is-active');
        if (activeColor) {
            const colorName = activeColor.dataset.colorName || activeColor.getAttribute('aria-label');
            if (colorName) config.push(`Color: ${colorName}`);
        }

        // Get selected upgrades
        this.state.selectedUpgrades.forEach((upgrade) => {
            config.push(`Upgrade: ${upgrade.name}`);
        });

        // Get selected options
        this.state.selectedOptions.forEach((option) => {
            config.push(`Option: ${option.name}`);
        });

        return config.join('\n');
    }
}

// Quote Modal Handler for Vehicle Configurator
class QuoteModalHandler {
    constructor() {
        console.log('QuoteModalHandler constructor called');
        this.modal = document.querySelector('[data-product-quote-modal]');
        console.log('Modal found:', this.modal);

        if (this.modal) {
            console.log('Initializing QuoteModalHandler...');
            this.init();
        } else {
            console.warn('Modal not found - QuoteModalHandler not initialized');
        }
    }

    init() {
        // Open modal buttons
        const requestQuoteBtns = document.querySelectorAll('[data-action="request-quote"]');
        console.log('Found request quote buttons:', requestQuoteBtns.length);

        requestQuoteBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
                console.log('Request quote button clicked');
                e.preventDefault();

                const modal = document.querySelector('[data-product-quote-modal]');
                console.log('Modal element:', modal);

                if (modal) {
                    try {
                        // Populate vehicle configurator data first
                        this.populateConfiguratorData();

                        // Open the modal
                        modal.showModal();
                        document.body.style.overflow = 'hidden';
                        console.log('Modal opened successfully');
                    } catch (error) {
                        console.error('Error opening modal:', error);
                    }
                } else {
                    console.error('Modal not found!');
                }
            });
        });

        // Search Inventory button
        document.querySelectorAll('[data-action="search-inventory"]').forEach(btn => {
            btn.addEventListener('click', () => {
                window.location.href = btn.getAttribute('data-url') || '/collections/all';
            });
        });
    }

    populateConfiguratorData() {
        const configurator = document.querySelector('.vehicle-configurator');
        if (!configurator) return;

        const vcInstance = configurator._vcInstance;
        if (!vcInstance) return;

        // Get selected package
        const selectedPackage = document.querySelector('.vc-accordion-item.is-active .vc-accordion-title');
        const packageName = selectedPackage ? selectedPackage.textContent.trim() : '';

        if (packageName) {
            const packageInput = document.querySelector('#SelectedXP');
            const packageDisplay = document.querySelector('[data-display-package]');
            if (packageInput) packageInput.value = packageName;
            if (packageDisplay) packageDisplay.textContent = packageName;
        }

        // Get selected color
        const selectedColorSwatch = document.querySelector('.vc-color-swatch.is-selected');
        const colorName = selectedColorSwatch ? (selectedColorSwatch.dataset.colorName || selectedColorSwatch.getAttribute('title') || '') : '';

        if (colorName) {
            const colorInput = document.querySelector('#SelectedColor');
            const colorDisplay = document.querySelector('[data-display-color]');
            const colorWrapper = document.querySelector('[data-display-color-wrapper]');

            if (colorInput) colorInput.value = colorName;
            if (colorDisplay) colorDisplay.textContent = colorName;
            if (colorWrapper) colorWrapper.style.display = 'flex';
        }

        // Get all selected options
        const selectedOptions = Array.from(document.querySelectorAll('.vc-option-card.is-selected'))
            .map(card => {
                const optionName = card.querySelector('.vc-option-name');
                return optionName ? optionName.textContent.trim() : '';
            })
            .filter(name => name)
            .join(', ');

        if (selectedOptions) {
            const optionsInput = document.querySelector('#UserSelection');
            const optionsDisplay = document.querySelector('[data-display-options]');
            const optionsWrapper = document.querySelector('[data-display-options-wrapper]');

            if (optionsInput) optionsInput.value = selectedOptions;
            if (optionsDisplay) optionsDisplay.textContent = selectedOptions;
            if (optionsWrapper) optionsWrapper.style.display = 'flex';
        }

        console.log('✅ Vehicle configurator data populated for quote modal');
    }
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.vehicle-configurator').forEach(container => {
        const instance = new VehicleConfigurator(container);
        container._vcInstance = instance; // Store instance for quote modal access
    });

    // Initialize quote modal
    new QuoteModalHandler();
});

if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
        const container = document.querySelector(`[data-section-id="${event.detail.sectionId}"]`);
        if (container && container.classList.contains('vehicle-configurator')) {
            new VehicleConfigurator(container);
        }
    });
}
